// useModerationStrikes — DB-backed strike-count för current user.
//
// Ersätter localStorage-based getStrikes/addStrike/setBanned. Läser från
// count_user_violations-RPC (rolling 24h window). Invalidering sker när
// en ny event record:ats via record_moderation_event.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { callRpc } from '@/lib/supabaseTyped';
import { useAuth } from '@/contexts/AuthContext';

export const MAX_STRIKES = 3;
export const STRIKE_WINDOW_HOURS = 24;

interface CountResponse {
  success: boolean;
  count?: number;
  latest_at?: string;
  error?: string;
}

export interface ModerationStrikes {
  strikeCount: number;
  maxStrikes: number;
  isBanned: boolean;
  latestStrikeAt: string | null;
  isLoading: boolean;
  refresh: () => void;
}

async function fetchStrikeCount(): Promise<{ count: number; latest: string | null }> {
  const { data, error } = await callRpc<CountResponse>('count_user_violations', {
    p_within_hours: STRIKE_WINDOW_HOURS,
  });
  if (error || !data?.success) {
    return { count: 0, latest: null };
  }
  return { count: data.count ?? 0, latest: data.latest_at ?? null };
}

/**
 * Nolla ut gamla localStorage-strikes vid första mount efter rollout.
 * Beställaren vald "nolla" på öppen fråga 7 i planen.
 *
 * BUG-FIX: tidigare raderades 'engicoro-ai-ban' men riktiga nyckeln är
 * 'engicoro-ai-banned' (legacyStrikesStorage.LEGACY_BAN_KEY). Cleanup
 * körde därmed aldrig på ban-flaggan → users som triggade legacy-banet
 * INNAN moderationV2 rolloutsen satt fast permanent. Vi raderar både
 * stavningar för att täcka in legacy-data.
 */
export function resetLegacyLocalStorageStrikes(): void {
  try {
    localStorage.removeItem('engicoro-ai-strikes');
    localStorage.removeItem('engicoro-ai-banned'); // korrekt nyckel
    localStorage.removeItem('engicoro-ai-ban');    // typo-fallback
  } catch {
    // Ignorera — privacy mode / ingen localStorage
  }
}

export function useModerationStrikes(): ModerationStrikes {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['moderation-strikes'],
    queryFn: fetchStrikeCount,
    enabled: isAuthenticated,
    staleTime: 10_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['moderation-strikes'] });
  }, [queryClient]);

  const count = query.data?.count ?? 0;
  return {
    strikeCount: count,
    maxStrikes: MAX_STRIKES,
    isBanned: count >= MAX_STRIKES,
    latestStrikeAt: query.data?.latest ?? null,
    isLoading: query.isLoading,
    refresh,
  };
}

/**
 * Record:a en moderation-event och invalidera cache.
 * Returnerar event-metadata (id + correlation) så caller kan länka till
 * eventuell appeal.
 */
export async function recordModerationEvent(args: {
  ruleId: string;
  ruleVersion: number;
  severity: 'advisory' | 'violation';
  humanReadable: string;
  matchedPattern?: string;
  correlationId?: string;
  conversationId?: string;
  messageId?: string;
}): Promise<{
  success: boolean;
  eventId?: string;
  correlationId?: string;
  error?: string;
}> {
  const { data, error } = await callRpc<{
    success: boolean;
    event_id?: string;
    correlation_id?: string;
    error?: string;
  }>('record_moderation_event', {
    p_rule_id: args.ruleId,
    p_rule_version: args.ruleVersion,
    p_severity: args.severity,
    p_human_readable: args.humanReadable,
    p_matched_pattern: args.matchedPattern,
    p_correlation_id: args.correlationId,
    p_conversation_id: args.conversationId,
    p_message_id: args.messageId,
  });

  if (error) return { success: false, error: error.message };
  if (!data?.success) return { success: false, error: data?.error };

  return {
    success: true,
    eventId: data.event_id,
    correlationId: data.correlation_id,
  };
}

/** Länka en event till en appeal (called från SubmitAppealDialog). */
export async function linkModerationToAppeal(
  eventId: string,
  appealId: string,
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await callRpc<{ success: boolean; error?: string }>(
    'link_moderation_to_appeal',
    { p_event_id: eventId, p_appeal_id: appealId },
  );
  if (error) return { success: false, error: error.message };
  if (!data?.success) return { success: false, error: data?.error };
  return { success: true };
}
